import './App.css';

import { OidcProvider } from '@axa-fr/react-oidc';
import {
  DevToolsInitializer,
  FontScaleContextProvider,
  MessageChannelContextProvider,
  UIConfigProvider,
} from '@healthhub/react-ui';
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';
import { PersistGate } from 'redux-persist/integration/react';

import { AUTH_CONFIG } from '@shared/configs/authentication';
import { router } from '@shared/configs/configs/router';
import {
  GlobalBreadcrumbsContextWrapper,
  LayoutViewBridge,
  PageTitleContextWrapper,
  StylesConfigContextWrapper,
} from '@shared/context';
import { persistor, store } from '@shared/stores';
import { initializeAxiosInterceptor } from '@shared/utils/api/interceptor';
import { MESSAGE_ACTION } from '@shared/utils/bridge';
import { enableConsoleLoggingForAllowedEnvironments } from '@shared/utils/console';

import { ROUTES } from '@features/index';

import './i18n';

interface ConfigAppProps {
  children?: React.ReactNode;
}

// Boot wiring, in dependency order: the bridge resolves a layout view from the route table for
// callers that run before `LayoutViewContextProvider` mounts, so it is seeded before anything else.
LayoutViewBridge.setRoutes(ROUTES);
enableConsoleLoggingForAllowedEnvironments();
initializeAxiosInterceptor();

export const ConfigApp = ({ children }: ConfigAppProps) => (
  <UIConfigProvider>
    <OidcProvider {...AUTH_CONFIG}>
      <PersistGate loading={null} persistor={persistor}>
        <Provider store={store}>
          <MessageChannelContextProvider messageAction={MESSAGE_ACTION}>
            <FontScaleContextProvider>
              <StylesConfigContextWrapper>
                <PageTitleContextWrapper>
                  <GlobalBreadcrumbsContextWrapper>{children}</GlobalBreadcrumbsContextWrapper>
                  {/* TODO: Re-enable DevToolsInitializer after demo */}
                  <DevToolsInitializer enableLogs={false} env={import.meta.env.MODE} /> 
                </PageTitleContextWrapper>
              </StylesConfigContextWrapper>
            </FontScaleContextProvider>
          </MessageChannelContextProvider>
        </Provider>
      </PersistGate>
    </OidcProvider>
  </UIConfigProvider>
);

const App = () => (
  <div className="App">
    <Suspense fallback={null}>
      <ConfigApp>
        <RouterProvider router={router} />
      </ConfigApp>
    </Suspense>
  </div>
);

export default App;
